import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { localeResources } from './locales/index.js';

export const SUPPORTED_LANGUAGES = ['en', 'pl'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = 'ig.lang';

function normalizeLang(value: string | null | undefined): SupportedLanguage | null {
  const code = value?.slice(0, 2).toLowerCase();
  if (code && (SUPPORTED_LANGUAGES as readonly string[]).includes(code)) {
    return code as SupportedLanguage;
  }
  return null;
}

function persistLanguage(lng: SupportedLanguage): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, lng);
    if (typeof document !== 'undefined') document.documentElement.lang = lng;
  } catch {
    /* ignore — blocked storage */
  }
}

function readStoredLang(): SupportedLanguage {
  try {
    const stored = normalizeLang(window.localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;

    const browser = normalizeLang(window.navigator.language);
    if (browser) return browser;
  } catch {
    /* ignore — SSR / blocked storage */
  }
  return 'en';
}

void i18n.use(initReactI18next).init({
  resources: localeResources,
  lng: readStoredLang(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

i18n.on('languageChanged', (lng) => {
  const normalized = normalizeLang(lng);
  if (!normalized) return;
  persistLanguage(normalized);
});

if (typeof document !== 'undefined') {
  const initial = normalizeLang(i18n.language) ?? 'en';
  document.documentElement.lang = initial;
}

export function setLanguage(lng: SupportedLanguage) {
  persistLanguage(lng);
  void i18n.changeLanguage(lng);
}

export default i18n;
